// @file Composable to handle attachment uploading for posts/drafts.
// NOTE: This composable is an extension of `surface_drafts` Pinia store
// so we MUST NOT destructure Pinia stores.
import { __ } from '@@/bits/intl'
import { getAttachmentsFromDraft } from '@@/bits/post_drafts'
import {
  FILE_TOO_LARGE_ERROR,
  startUpload,
  stopUpload,
  VIDEO_TRIM_FAILED_ERROR,
  type UploadPurpose,
} from '@@/bits/uploader'
import { unboundedWatch } from '@@/bits/vue'
import { useComposerModalAlertStore } from '@@/pinia/composer_modal_alert'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid, UploadedFile } from '@@/types'
import useLogger from '@@/vuecomposables/logger'
import { uniqueId } from 'es-toolkit/compat'
import { computed } from 'vue'

const logger = useLogger('post-attachment-uploader', { logLifecycleHooks: false, log: true })

export const usePostAttachmentUploader = (): {
  startUploadFile: typeof startUploadFile
  stopUploadFile: typeof stopUploadFile
} => {
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const composerModalAlertStore = useComposerModalAlertStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()

  /**
   * Assigns a unique upload ID to a post attachment
   * and updates the draft attachments array if an attachment index is provided
   * @param cid - The content ID of the draft/post
   * @param attachmentIndex - Optional index of the attachment in the draft's attachments array
   * @returns The generated unique attachment upload ID
   */
  const assignUniqueIdToPhotoAlbumAttachment = (cid: Cid, attachmentIndex?: number): string => {
    const uniqueAttachmentId = uniqueId(`post-attachment-uploader-${cid}`)
    if (attachmentIndex != null) {
      const draft = surfaceDraftsStore.draftByCid[cid]
      if (draft?.attachments?.[attachmentIndex] != null) {
        const updatedAttachments = [...draft.attachments]
        updatedAttachments[attachmentIndex] = {
          ...updatedAttachments[attachmentIndex],
          uploadId: uniqueAttachmentId,
        }
        surfaceDraftsStore.updateDraft({ cid, attachments: updatedAttachments })
      }
    }
    return uniqueAttachmentId
  }

  const startUploadFile = ({
    file,
    maxFileUploadSize,
    cid,
    trimVideoAfterUpload,
    attachmentIndex,
    uploadPurpose,
    recordingDuration,
  }: {
    file: UploadedFile
    maxFileUploadSize?: number
    cid: Cid
    trimVideoAfterUpload?: { start: number; end: number }
    attachmentIndex?: number
    uploadPurpose?: UploadPurpose
    recordingDuration?: number
  }): void => {
    // Google Drive files are uploaded by Beethoven, we don't do it here.
    if (file.isGoogleDriveFile === true) return

    const currentDraft = surfaceDraftsStore.draftByCid[cid]
    const uniqueAttachmentId = assignUniqueIdToPhotoAlbumAttachment(cid, attachmentIndex)

    const uploadJob = startUpload(file, {
      maxFileSize: maxFileUploadSize,
      trimVideoAfterUpload,
      uploadPurpose,
      recordingDuration,
    })

    if (trimVideoAfterUpload != null) {
      showVideoTrimmingSnackbar()
    }

    uploadJob.on('progress', (progress) => {
      logger.log(`Upload job progress: ${progress as unknown as number}`)
      if (attachmentIndex == null) {
        surfaceDraftsStore.updateDraft({ cid, uploadProgress: progress })
        return
      }
      const draft = surfaceDraftsStore.draftByCid[cid]
      if (draft == null || draft.attachments == null) return

      const attachmentToUpdateIndex = draft.attachments.findIndex((att) => att.uploadId === uniqueAttachmentId)
      if (attachmentToUpdateIndex === -1) return

      const updatedAttachments = [...draft.attachments]
      if (updatedAttachments[attachmentToUpdateIndex] != null) {
        updatedAttachments[attachmentToUpdateIndex] = {
          ...updatedAttachments[attachmentToUpdateIndex],
          progress,
        }
        surfaceDraftsStore.updateDraft({ cid, attachments: updatedAttachments })
      }
    })

    const attachmentKey = attachmentIndex != null ? uniqueAttachmentId : undefined
    uploadJob.on('done', ({ url }) => {
      logger.log(`Upload job done: ${url as unknown as string}`)
      deleteUploadedFile({ cid, attachmentKey })
      if (attachmentIndex == null) {
        surfaceDraftsStore.updateDraft({
          cid,
          attachment: url,
          wish_content: {
            attachment_props: { url },
            is_processed: false,
          },
          uploadingFile: null,
        })
      } else {
        const draft = surfaceDraftsStore.draftByCid[cid]
        if (draft == null) return

        const attachments = getAttachmentsFromDraft(draft)
        const attachmentToUpdateIndex = attachments.findIndex((att) => att.uploadId === uniqueAttachmentId)
        if (attachmentToUpdateIndex === -1) return

        if (attachments[attachmentToUpdateIndex] != null) {
          // remove upload-related fields from the attachment
          const { isUploadingFile, fileBeingUploaded, progress, uploadId, ...rest } =
            attachments[attachmentToUpdateIndex]

          attachments[attachmentToUpdateIndex] = {
            ...rest,
            url,
          }
          surfaceDraftsStore.updateDraft({
            cid,
            attachments,
          })
        }
      }

      const updatedCurrentDraft = surfaceDraftsStore.draftByCid[cid]
      if (updatedCurrentDraft?.shouldAutoPublish === true) {
        const areAllAttachmentsUploaded = updatedCurrentDraft.attachments?.every((a) => !a.isUploadingFile) ?? true

        if (areAllAttachmentsUploaded) {
          surfaceDraftsStore.stopAutoPublishDraft(cid) // when upload is done, stop auto publish
          void surfacePostsStore.savePost({ attributes: updatedCurrentDraft })
        }
      }
    })

    uploadJob.on('error', (err) => {
      logger.log(`Upload job done: ${err as unknown as string}`)
      deleteUploadedFile({ cid, attachmentKey })
      if (attachmentIndex == null) {
        surfaceDraftsStore.updateDraft({ cid, uploadError: `Upload job failed: ${Number(err.status)}` })
        surfaceDraftsStore.stopUploadFileInDraft(cid)
        surfaceDraftsStore.stopAutoPublishDraft(cid)
      } else {
        const draft = surfaceDraftsStore.draftByCid[cid]
        if (draft?.attachments != null) {
          const updatedAttachments = [...draft.attachments]
          const attachmentToUpdateIndex = updatedAttachments.findIndex((att) => att.uploadId === uniqueAttachmentId)

          if (attachmentToUpdateIndex !== -1) {
            updatedAttachments[attachmentToUpdateIndex] = {
              ...updatedAttachments[attachmentToUpdateIndex],
              isUploadingFile: false,
              fileBeingUploaded: undefined,
              progress: 0,
              error: `Upload job failed: ${Number(err.status)}`,
              uploadId: undefined,
            } as any
            surfaceDraftsStore.updateDraft({ cid, attachments: updatedAttachments })
          }
        }
      }

      if (err.error === FILE_TOO_LARGE_ERROR) {
        surfaceStore.showOverFileSizeModal({ file })
        return
      }

      const message = __('Sorry, there was an error uploading your file.')

      if (
        surfaceDraftsStore.isCidActiveDraft(cid) &&
        err.error !== VIDEO_TRIM_FAILED_ERROR // if error is VIDEO_TRIM_FAILED, show the composer alert instead of the snackbar.
      ) {
        globalSnackbarStore.setSnackbar({ message, notificationType: SnackbarNotificationType.error })
        return
      }

      composerModalAlertStore.openComposerAlert({
        title: __('There was an error loading the attachment'),
        body: message,
        primaryActionText: __('Try again'),
        secondaryActionText: __('Cancel'),
        primaryActions: [
          () =>
            surfaceDraftsStore.startUploadFileInDraft({
              cid,
              file,
              maxFileUploadSize: currentDraft?.maxFileUploadSize,
            }),
        ],
        secondaryActions: [],
      })
    })

    surfaceDraftsStore.setUploadJob({ cid, uploadJob, attachmentKey })
  }

  const deleteUploadedFile = ({ cid, attachmentKey }: { cid: Cid; attachmentKey?: string }): void => {
    const uploader = surfaceDraftsStore.getUploadJob(cid, attachmentKey)
    if (uploader == null) return
    stopUpload(uploader.file)
    surfaceDraftsStore.deleteUploadJob({ cid, attachmentKey })
  }

  const stopUploadFile = ({ cid, attachmentIndex }: { cid: Cid; attachmentIndex?: number }): void => {
    const draft = surfaceDraftsStore.draftByCid[cid]
    if (draft == null) return

    const attachmentKey = attachmentIndex != null ? draft.attachments?.[attachmentIndex]?.uploadId : undefined

    deleteUploadedFile({ cid, attachmentKey })
    surfaceDraftsStore.stopAutoPublishDraft(cid)

    if (attachmentIndex == null) {
      surfaceDraftsStore.updateDraft({ cid, uploadingFile: null, uploadProgress: 0 })
    } else {
      if (draft.attachments != null) {
        const updatedAttachments = [...draft.attachments]
        if (updatedAttachments[attachmentIndex] != null) {
          updatedAttachments[attachmentIndex] = {
            ...updatedAttachments[attachmentIndex],
            isUploadingFile: false,
            fileBeingUploaded: undefined,
            progress: 0,
            uploadId: undefined,
          }
          surfaceDraftsStore.updateDraft({ cid, attachments: updatedAttachments })
        }
      }
    }
  }

  const pendingPostReadyToUploadCids = computed<Cid[]>(() => {
    if (surfacePostsStore.pendingPosts == null) return []
    const pendingPostsWithFile = surfacePostsStore.pendingPosts.filter((post) => Boolean(post.file))

    // If there are posts for which sections are yet to be picked,
    // don't start uploading them, filter them out. Only do it for
    // sectioned walls because that's the only occasion where we
    // ask users to pick a section.
    if (surfaceStore.canUseSections && surfaceStore.isSectioned) {
      const pendingPostsWithSections = pendingPostsWithFile.filter((x) => Boolean(x.wall_section_id))
      return pendingPostsWithSections.map((post) => post.cid)
    }

    return pendingPostsWithFile.map((post) => post.cid)
  })

  void unboundedWatch(pendingPostReadyToUploadCids, (cids) => {
    if (cids.length === 0) return
    cids.forEach((cid) => {
      const postAttributes = surfacePostsStore.pendingPostEntitiesByCid[cid]
      if (postAttributes.file == null) return
      surfaceDraftsStore.createNewDraftFromPendingUpload({
        file: postAttributes.file,
        postAttributes,
      })
    })
    surfacePostsStore.removePendingPosts({ cids })
  })

  const showVideoTrimmingSnackbar = (): void => {
    globalSnackbarStore.setSnackbar({
      message: __('Your video is processing. This may take a few minutes.'),
    })
  }

  return { startUploadFile, stopUploadFile }
}
